// THE APP'S OWN WORDS FOR THE SCREENS BEFORE PAYMENT (WEB-PARITY-2B; `docs/WEB_APP_PARITY.md` §2.1-2.6).
//
// Tyler, 2026-09-29: "the site should replicate exactly what the app has at this time" and "It should model the app
// perfectly". `/`, `/not-available`, `/account` and `/checkout` now say what the iPhone app says at the same moment —
// its entry screen (`AppealEntryFlow.swift`), its "still finishing" sheet, its property-kind picker and refusal sheet
// (`PropertyTypeSupport.swift`), its onboarding consent (`OnboardingView.swift`, `Disclaimers.swift`), its purchase sheet
// (`BuyPacketsSheet.swift`), its credit-confirm sheet and its compass-direction sheet. Every entry is the app's string
// literal on the line it cites (under `AppealAce/`), with the app's `\(…)` interpolations written `{name}`; nothing here
// is a new sentence, so each carries Tyler's standing instruction (`WEB_PARITY`) and no approval of its own.
// `web/test/prePayCopy.test.tsx` reads every cited line and fails on any difference.
//
// TWO ARE CUTS, AND EACH CUT IS NAMED (`cut`). A cut drops words; it never adds one, and the test holds that the text is
// the literal's opening sentence:
//   - the purchase hero's body keeps its first sentence. Its second, "Editing and re-exporting stays free for the life of
//     that appeal.", was item 6 of Tyler's approved "What a credit gets you" card on the same page (D6), and was not said
//     twice. That card left the page on 2026-10-13 (R251); the cut stays, because Tyler asked for the
//     panel the sub heads to stay exactly as it is ("i like the top part of this page");
//   - the compass sheet's footnote keeps its first sentence. Its second ended "and it isn't refundable", which the
//     website's approved Refund Policy contradicts (a credit is returned when the wrong property was looked up, among
//     other cases), so it is a legal sentence for Tyler, not a parity port (docs/WEB_READINESS.md, F-WP2B-1). Since
//     `main`'s R175 the app's second sentence is M3 (approved 2026-09-30, "on both clients"); whether the website adds
//     M3 after the first sentence is the orchestrator's (R233, F-SYNC-4), and until then it is cut.
//
// A SENTENCE THE SERVER STATES IS NOT HERE. The refusal bodies (`resolve.g5.*`), the coming-soon body
// (`resolve.g1.comingSoon`), G-2's card and T-1's protest-window notice arrive as `Copy` on a response. What is here is
// the app's chrome around them — titles, labels, buttons — and the sentences no endpoint states.

import { WEB_PARITY, type AppWording } from "./heldCopy";

/** An app string on the website. `cut` names the words dropped, when the text is the literal's opening sentence only. */
export interface PrePayWording extends AppWording {
  readonly cut: string | null;
}

function app(appSource: string, text: string, cut: string | null = null): PrePayWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource, cut };
}

const ENTRY = "Features/Flow/AppealEntryFlow.swift";
const TYPES = "Services/PropertyTypeSupport.swift";
const PURCHASE = "Features/Purchase/BuyPacketsSheet.swift";
const CAN_RAISE = "Services/CanRaise.swift";
const DISCLAIMERS = "Shared/Disclaimers.swift";
const ONBOARDING = "Features/Onboarding/OnboardingView.swift";
const CHECKER = "Features/Checker/PropertyCheckerView.swift";

export const PRE_PAY_WORDING = {
  // ── `/`: the app's "Start your appeal" screen (EN-1 to EN-4, EN-2, PR-1, PR-2) ──
  "entry.navTitle": app(`${ENTRY}:204`, "Start your appeal"),
  "entry.hero.title": app(`${ENTRY}:650`, "Are you overpaying on property tax?"),
  "entry.hero.body": app(
    `${ENTRY}:653`,
    "Enter your address. We pull real comparable sales, tell you honestly whether you have a case, and build the letter when the numbers support it.",
  ),
  "entry.address.title": app(`${ENTRY}:695`, "Property address"),
  "entry.address.placeholder": app(`${ENTRY}:700`, "Start typing your address"),
  "entry.address.hint": app(
    "Shared/AddressAutocompleteField.swift:136",
    "Not in the list? Use the address exactly as you typed it — you don't have to pick a suggestion.",
  ),
  // The unit field's footer, rendered from the requirement (`SubjectProperty.unitFieldFooter`).
  "entry.unit.required": app(
    "Models/Models.swift:977",
    "Required for a condominium — each unit is assessed as its own parcel, so without it we'd look up the building instead of your home.",
  ),
  "entry.unit.optional": app("Models/Models.swift:978", "Leave blank for a house or townhouse."),
  "entry.kind.title": app(`${ENTRY}:239`, "What kind of property?"),
  "entry.kind.select": app(`${ENTRY}:245`, "Select"),
  // The picker's labels (PR-1). The four served types are `PropertyTypeSupport.supported`, one array on one line.
  "entry.kind.singleFamily": app(`${TYPES}:32`, "Single-family"),
  "entry.kind.twin": app(`${TYPES}:32`, "Twin / Semi-detached"),
  "entry.kind.townhouse": app(`${TYPES}:32`, "Townhouse"),
  "entry.kind.condo": app(`${TYPES}:32`, "Condo"),
  "entry.kind.multiUnit": app(`${ENTRY}:247`, "Multi-unit rental building"),
  "entry.kind.manufactured": app(`${ENTRY}:248`, "Manufactured / mobile home"),
  "entry.kind.other": app(`${ENTRY}:249`, "Something else"),
  // What a credit covers, under the picker (PR-2).
  "entry.kind.coverage": app(
    `${TYPES}:139`,
    "Each credit covers a single-family home, twin/semi-detached, townhouse, or condominium. Multi-unit rental buildings and manufactured/mobile homes aren't supported yet.",
  ),
  // The refusal sheet's titles, by G-5's refusal (PR-3), and its one button (PR-4).
  "refusal.manufactured.title": app(`${TYPES}:49`, "We're not ready for manufactured homes yet"),
  "refusal.multiUnit.title": app(`${TYPES}:50`, "We're not ready for rental buildings yet"),
  "refusal.unclassified.title": app(`${TYPES}:51`, "We need to know what kind of property this is"),
  "refusal.choose": app(`${ENTRY}:308`, "Choose a different property"),

  // ── `/not-available`: the app's "still finishing" sheet (NA-1, NA-4) ──
  "notAvailable.title": app(`${ENTRY}:330`, "We're still finishing {state}"),
  "notAvailable.tryAnother": app(`${ENTRY}:346`, "Try a different address"),

  // ── `/checkout`: the can-raise card of the app's purchase sheet (RK-1 to RK-3), titled by the card it heads. Since
  //    R251 one line under the Buy button, the title then `canRaise.confirmLater` (Tyler, 2026-10-13:
  //    "for raise.. B") ──
  "canRaise.title.pa": app(`${CAN_RAISE}:396`, "Pennsylvania can raise your assessment"),
  "canRaise.title.canRaise": app(`${CAN_RAISE}:667`, "{state} can raise your assessment"),
  "canRaise.title.unknown": app(`${CAN_RAISE}:681`, "Check whether your board can raise you"),
  "canRaise.confirmLater": app(
    `${PURCHASE}:407`,
    "You'll be asked to confirm you understand this before we generate anything.",
  ),

  // ── `/checkout`: the app's entry card at a zero balance (WALK-FIX-2; AEF:761-797). The app draws "Get credits" (it
  //    opens the purchase sheet) and this line instead of "Use 1 credit", and never offers a credit the owner does not
  //    have (`hasSpendableCredit`, AEF:1044-1049). The literal on :786 is "Get credits" when the price is not loaded,
  //    which is the one the website uses: the prices are on the Buy card the button goes to.
  //    SINCE R251 (Tyler, 2026-10-13) the website has no balance card at zero — the Buy card is the
  //    page's next block and carries the balance — so `entry.outOfCredits` renders nowhere, and "Get credits" is the
  //    ghost button that unfolds the Buy card for an owner who already has a credit and wants more: the same act, opening
  //    the purchase sheet, in the app's own words.
  "entry.getCredits": app(`${ENTRY}:808`, "Get credits"),
  "entry.outOfCredits": app(`${ENTRY}:817`, "You're out of credits. Buy more to unlock PROppeal."),

  // ── `/checkout`: the app's purchase sheet (CO-3, CO-4, CO-6) ──
  "purchase.hero.title": app(`${PURCHASE}:306`, "One credit, one appeal"),
  "purchase.hero.body": app(
    `${PURCHASE}:310`,
    "Each credit unlocks PROppeal for one property, one tax year.",
    "Editing and re-exporting stays free for the life of that appeal.",
  ),
  "purchase.bundles.title": app(`${PURCHASE}:439`, "Choose a bundle"),
  "purchase.bundle.one": app(`${PURCHASE}:92`, "1 credit"),
  "purchase.bundle.many": app(`${PURCHASE}:92`, "{quantity} credits"),
  "purchase.buy": app(`${PURCHASE}:552`, "Buy {label} · {price}"),
  "purchase.returning.title": app(`${PURCHASE}:615`, "Welcome back"),
  "purchase.returning.body": app(
    `${PURCHASE}:618`,
    "You logged your county's decision on a past appeal, so your next one is at the returning-owner price.",
  ),
  "purchase.returning.buy": app(`${PURCHASE}:623`, "Start an appeal — {price}"),

  // ── `/checkout`: the app's credit-confirm sheet (CO-1) ──
  "confirm.title": app(`${ENTRY}:429`, "Use your credit on this home?"),
  // AEF:413 interpolates " for \(address)" only when there is an address; the two readings are held whole.
  "confirm.covers": app(`${ENTRY}:439`, "One credit covers your entire appeal for {address} — start to finish."),
  "confirm.coversNoAddress": app(`${ENTRY}:439`, "One credit covers your entire appeal — start to finish."),
  "confirm.unit": app(`${ENTRY}:443`, "Unit {unit} — we'll appeal this unit's own parcel, not the building."),
  "confirm.directional": app(
    `${ENTRY}:467`,
    "The map placed this at a different compass direction than you typed, so we kept your address exactly as you entered it. North, South, East and West versions of the same street are different properties — check the direction is right before you spend.",
  ),
  // The cover rows. The sixth ("Write and rewrite your letter up to N times…", AEF:468) is left out on Tyler's ruling
  // for this page: "the what a letter covers page needs to not have the number of iterations and revisions you can do"
  // (WEB-BENEFITS, 2026-09-29).
  "confirm.row.comps": app(`${ENTRY}:482`, "Real recent comparable sales near your home, adjusted to your property"),
  "confirm.row.verdict": app(`${ENTRY}:483`, "An honest verdict on whether you have a case — even when it's a no"),
  "confirm.row.value": app(`${ENTRY}:484`, "Your estimated market value, with the math shown"),
  "confirm.row.letter": app(`${ENTRY}:485`, "The board-ready appeal letter + county portal summary"),
  "confirm.row.deadline": app(`${ENTRY}:486`, "Your county's filing deadline and appeal rules"),
  // M3, the sheet's line under the cover rows (AEF:481): APPROVED by Tyler on 2026-09-30 ("Sentences approved",
  // ACCOUNTS_PLAN §3 M) "on both clients". WEB-DESIGN-1 shows it on `/checkout`'s before-you-pay panel, as the app's sheet
  // does under the same rows. The compass sheet's copy of its first sentence stays cut (F-SYNC-4, below).
  "confirm.m3": app(
    `${ENTRY}:503`,
    "A credit is used when you confirm the property address and we start gathering comparable sales. In return, you get the complete appeal — the comparable-sales evaluation, your market-value analysis, and the letter — plus an honest answer either way, even if we find you don't have a strong case. It's not a guarantee of a lower assessment.",
  ),
  "confirm.use": app(`${ENTRY}:588`, "Use my credit"),
  "confirm.using": app(`${ENTRY}:585`, "Applying your credit…"),
  "confirm.notYet": app(`${ENTRY}:596`, "Not yet"),

  // ── `/checkout`: the app's compass-direction sheet, in front of the confirm sheet (CO-2) ──
  "compass.title": app(`${ENTRY}:885`, "Check the compass direction"),
  // The sheet's `directionalConflicted` reading — the one arm the server computes (G-3 `directionalConflict`).
  "compass.body": app(
    `${ENTRY}:890`,
    "The map placed this at a different compass direction than you typed, so we kept your address exactly as you entered it.",
  ),
  "compass.different": app(
    `${ENTRY}:904`,
    "North, South, East and West versions of the same street are different properties.",
    // Since `main`'s R175 the app's second sentence is M3 (Tyler, 2026-09-30, "Sentences approved"), which replaced
    // "Your credit is spent on the address above, and it isn't refundable." The website still shows the first sentence
    // alone, so only the words it cuts changed (R233).
    "A credit is used when you confirm the property address and we start gathering comparable sales.",
  ),
  "compass.fix": app(`${ENTRY}:914`, "Go back and fix the address"),
  "compass.anyway": app(`${ENTRY}:929`, "Use {address} anyway — I meant this one"),

  // ── `/checkout`: the free check's first look and its pop-up (R-pending-web-free-first; Tyler, 2026-10-14, "A", "Yes on
  //    layout"). The app's checker words for the same thing, each approved there (Tyler, 2026-09-22: "I approve those 6"):
  //    its title, heading the band and the pop-up; its "checking" line, holding the band's place while the server answers;
  //    and the word on its button that closes it. Every sentence INSIDE the band and the pop-up is the server's. ──
  "freeFirst.title": app(`${CHECKER}:145`, "A free first look at your home"),
  "freeFirst.running": app(`${CHECKER}:222`, "Running your free check…"),
  "freeFirst.close": app(`${CHECKER}:125`, "Close"),

  // ── `/account`: the app's onboarding consent (AC-3 to AC-5) ──
  "assent.finePrint.title": app(`${DISCLAIMERS}:58`, "What PROppeal is — and isn't"),
  "assent.finePrint.read": app(`${ONBOARDING}:459`, "Read"),
  "assent.finePrint.hide": app(`${ONBOARDING}:459`, "Hide"),
  "assent.point.1": app(
    `${DISCLAIMERS}:61`,
    "We help you build a strong, comp-backed appeal — then you file it yourself with your county. We're your toolkit, not your representative.",
  ),
  "assent.point.2": app(
    `${DISCLAIMERS}:62`,
    "Think of PROppeal as a smart assistant, not an attorney or appraiser. Your letter and comps aren't legal advice or a certified appraisal.",
  ),
  "assent.point.3": app(
    `${DISCLAIMERS}:63`,
    "No one can promise a reduction — the board decides. What we can do is help you put your best, best-organized case in front of them.",
  ),
  "assent.point.4": app(
    `${DISCLAIMERS}:72`,
    "In some states the board can also raise an assessment on appeal, not just lower it. Once we know where your property is, we'll tell you exactly what your state allows — before you decide whether to file.",
  ),
  "assent.point.5": app(
    `${DISCLAIMERS}:73`,
    "We pull from public records and what you enter, so give the details a quick check before filing. Accurate info makes a stronger case.",
  ),
  "assent.point.6": app(
    `${DISCLAIMERS}:74`,
    "Your address and property details stay yours — we use them only to build your letter, and we never sell your data.",
  ),
  // `Disclaimers.entityLine` interpolates `legalEntity` (the line above it), held here as the app renders it.
  "assent.entity": app(`${DISCLAIMERS}:20`, "PROppeal is a product of {legalEntity}, a Nebraska limited liability company."),
  "assent.entity.name": app(`${DISCLAIMERS}:19`, "BrightBench LLC"),
  "assent.getStarted": app(`${ONBOARDING}:608`, "Get started"),
  "assent.tickFirst": app(`${ONBOARDING}:628`, "Tick the box above to continue"),
} as const satisfies Record<string, PrePayWording>;

export type PrePayId = keyof typeof PRE_PAY_WORDING;

/** The six points, in the app's order. */
export const ASSENT_POINTS = [
  "assent.point.1", "assent.point.2", "assent.point.3", "assent.point.4", "assent.point.5", "assent.point.6",
] as const satisfies readonly PrePayId[];

/** The five cover rows, in the app's order. The before-you-pay panel lists them; since R251 the
 *  credit pop-up does not repeat them (Tyler, 2026-10-13: the confirmation "looks the same as before"). */
export const CONFIRM_ROWS = [
  "confirm.row.comps", "confirm.row.verdict", "confirm.row.value", "confirm.row.letter", "confirm.row.deadline",
] as const satisfies readonly PrePayId[];

/** The words of one entry. The element that shows them carries `data-app-copy` with the id. */
export function prePayText(id: PrePayId): string {
  return PRE_PAY_WORDING[id].text;
}

/** An entry with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function prePayTemplate(id: PrePayId, values: Readonly<Record<string, string | number>>): string {
  return prePayText(id).replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`prePayTemplate(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}

/** The entity line as the app renders it: `Disclaimers.entityLine` with `legalEntity` in it. */
export function entityLine(): string {
  return prePayTemplate("assent.entity", { legalEntity: prePayText("assent.entity.name") });
}
